import React, { createContext, useContext, useEffect, useState } from "react";
import Papa from "papaparse";
import { getDownloadURL, ref } from "firebase/storage";
import { storage } from "../components/register/firebase"; // path to your firebase.js"
import { getAuth } from "firebase/auth";

import { getFirestore, doc, getDoc } from "firebase/firestore";

const DashboardContext = createContext();

export const useDashboard = () => {
  const context = useContext(DashboardContext);
  if (!context) {
    throw new Error("useDashboard must be used within a DashboardProvider");
  }
  return context;
};

export const DashboardProvider = ({ children }) => {
  const [dashboardData, setDashboardData] = useState(null);

  useEffect(() => {
    const fetchCSVData = async () => {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) {
        console.log("No user found.");
        return;
      }

      try {
        const uid = user.uid;
        const fileRef = ref(storage, `temp_csv/${uid}/trading_data_sample.csv`);
        const url = await getDownloadURL(fileRef);

        const response = await fetch(url);
        const csvText = await response.text();

        const db = getFirestore();
        const userDocRef = doc(db, "users", uid);
        const userDocSnap = await getDoc(userDocRef);

        if (!userDocSnap.exists()) {
          console.log("No such user document!");
          return;
        }

        const userProfile = userDocSnap.data();

        Papa.parse(csvText, {
          header: true,
          skipEmptyLines: true,
          complete: (results) => {
            const data = results.data;

            if (data.length === 0) {
              console.log("No activity data found.");
              return;
            }

            const latest = data[data.length - 1];

            setDashboardData({
              profile: {
                name: userProfile.name || "Trader",
                tagline: "Making moves in the market",
                contact: userProfile.phoneNumber || "",
                avatar: userProfile.name?.[0]?.toUpperCase() || "T",
                isVerified: userProfile.isPhoneVerified || false,
              },
              metrics: {
                netProfitLoss: {
                  value: parseFloat(latest.total_pnl).toLocaleString("en-IN", {
                    style: "currency",
                    currency: "INR",
                    maximumFractionDigits: 2,
                  }),
                  period: {
                    from: data[0].date,
                    to: latest.date,
                  },
                },
                currentStreak: {
                  days: parseInt(latest.current_streak || "0", 10),
                  from: "", // Optional: you can track starting date of streak separately
                },
                longestStreak: {
                  days: parseInt(latest.longest_streak || "0", 10),
                  period: {
                    from: "", // Optional
                    to: "",
                  },
                },
                mostProfitableDay: {
                  date: latest.most_profit_day,
                  value: parseFloat(latest.most_profit).toLocaleString(
                    "en-IN",
                    {
                      style: "currency",
                      currency: "INR",
                      maximumFractionDigits: 2,
                    }
                  ),
                },
                equity: parseFloat(latest.total_equity_pnl).toLocaleString(
                  "en-IN",
                  {
                    style: "currency",
                    currency: "INR",
                    maximumFractionDigits: 2,
                  }
                ),
                fAndO: parseFloat(latest.total_fno_pnl).toLocaleString(
                  "en-IN",
                  {
                    style: "currency",
                    currency: "INR",
                    maximumFractionDigits: 2,
                  }
                ),
              },
              activityData: data,
            });
          },
          error: (error) => {
            console.error("Error parsing CSV:", error);
          },
        });
      } catch (error) {
        console.error("Error fetching CSV file:", error);
      }
    };

    fetchCSVData();
  }, []);

  if (!dashboardData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-gray-500 text-lg">Loading Dashboard...</div>
      </div>
    );
  }

  return (
    <DashboardContext.Provider value={dashboardData}>
      {children}
    </DashboardContext.Provider>
  );
};
